import { useState, useEffect, useCallback, useRef } from 'react';
import axios from '../../axiosInstance';
import {
    collaborationSocket,
    // notificationsSocket,
    connectNamespace,
    disconnectNamespace,
} from '../../lib/socket';

// ── Styles ──

const colors = {
    bg: '#0d1117', card: '#161b22', border: '#30363d', text: '#c9d1d9',
    muted: '#8b949e', green: '#3fb950', red: '#f85149', blue: '#58a6ff',
    yellow: '#d29922', purple: '#bc8cff',
};

const dot = (on) => ({
    display: 'inline-block', width: 8, height: 8, borderRadius: '50%',
    backgroundColor: on ? colors.green : colors.red, marginRight: 8,
});

const btn = (color = colors.border) => ({
    padding: '6px 14px', cursor: 'pointer', border: `1px solid ${color}`,
    borderRadius: 6, background: 'transparent', color: colors.text,
    fontSize: 13, fontFamily: 'inherit',
});

const card = { background: colors.card, border: `1px solid ${colors.border}`, borderRadius: 8, padding: 16, marginBottom: 16 };
const label = { fontSize: 11, textTransform: 'uppercase', color: colors.muted, letterSpacing: 1, marginBottom: 8 };
const input = { background: colors.bg, border: `1px solid ${colors.border}`, borderRadius: 4, padding: '6px 10px', color: colors.text, fontSize: 13, fontFamily: 'inherit', width: 200 };

const logColors = {
    COLLAB: colors.blue, NOTIF: colors.purple, ERROR: colors.red,
    ROOM: colors.green, CURSOR: colors.yellow, PRESENCE: colors.yellow,
    API: colors.muted,
};

function colorize(text) {
    for (const [key, color] of Object.entries(logColors)) {
        if (text.includes(key)) return color;
    }
    return colors.text;
}

// ── Component ──

const SocketTest = () => {
    const [collabConnected, setCollabConnected] = useState(false);
    const [notifConnected, setNotifConnected] = useState(false);
    const [logs, setLogs] = useState([]);
    const [diagramId, setDiagramId] = useState('test-diagram-1');
    const [inRoom, setInRoom] = useState(null);
    const [roomMembers, setRoomMembers] = useState([]);
    const logRef = useRef(null);

    const addLog = useCallback((tag, msg) => {
        const time = new Date().toLocaleTimeString('en', { hour12: false });
        setLogs((prev) => [`[${time}] [${tag}] ${msg}`, ...prev].slice(0, 200));
    }, []);

    // ── Socket event listeners ──
    useEffect(() => {
        // --- Collaboration namespace ---
        collaborationSocket.on('connect', () => {
            setCollabConnected(true);
            addLog('COLLAB', `connected (id: ${collaborationSocket.id})`);
        });
        collaborationSocket.on('disconnect', (reason) => {
            setCollabConnected(false);
            setInRoom(null);
            setRoomMembers([]);
            addLog('COLLAB', `disconnected (${reason})`);
        });
        collaborationSocket.on('connect_error', (err) => {
            addLog('ERROR', `collab connect_error: ${err.message}`);
        });
        collaborationSocket.on('session_expired', () => {
            addLog('ERROR', 'collab session_expired — server will disconnect');
        });

        // Room events
        collaborationSocket.on('member:joined', ({ userId, userName }) => {
            addLog('ROOM', `member:joined — ${userName} (${userId})`);
            setRoomMembers((prev) => [...prev.filter((m) => m.userId !== userId), { userId, userName }]);
        });
        collaborationSocket.on('member:left', ({ userId, userName }) => {
            addLog('ROOM', `member:left — ${userName} (${userId})`);
            setRoomMembers((prev) => prev.filter((m) => m.userId !== userId));
        });

        // Cursor + presence from other users
        collaborationSocket.on('cursor:moved', ({ userId, userName, x, y }) => {
            addLog('CURSOR', `${userName}: (${x}, ${y})`);
        });
        collaborationSocket.on('presence:changed', ({ userId, userName, status }) => {
            addLog('PRESENCE', `${userName}: ${status}`);
        });

        // Diagram updates from event bus
        collaborationSocket.on('diagram:updated', ({ diagramId, changes }) => {
            addLog('COLLAB', `diagram:updated [${diagramId}] ${JSON.stringify(changes)}`);
        });

        // // --- Notifications namespace ---
        // notificationsSocket.on('connect', () => {
        //     setNotifConnected(true);
        //     addLog('NOTIF', `connected (id: ${notificationsSocket.id})`);
        // });
        // notificationsSocket.on('disconnect', (reason) => {
        //     setNotifConnected(false);
        //     addLog('NOTIF', `disconnected (${reason})`);
        // });
        // notificationsSocket.on('connect_error', (err) => {
        //     addLog('ERROR', `notif connect_error: ${err.message}`);
        // });
        // notificationsSocket.on('session_expired', () => {
        //     addLog('ERROR', 'notif session_expired — server will disconnect');
        // });

        // // Notification events
        // notificationsSocket.on('notification:invite', (data) => {
        //     addLog('NOTIF', `invite — diagram "${data.diagramName}" by ${data.invitedBy}`);
        // });
        // notificationsSocket.on('notification:member_added', (data) => {
        //     addLog('NOTIF', `member_added — diagram "${data.diagramName}"`);
        // });
        // notificationsSocket.on('notification:member_removed', (data) => {
        //     addLog('NOTIF', `member_removed — diagram "${data.diagramName}"`);
        // });
        // notificationsSocket.on('notification:diagram_updated', (data) => {
        //     addLog('NOTIF', `diagram_updated — "${data.diagramName}" by ${data.updatedBy}`);
        // });
        // notificationsSocket.on('notification:generic', (data) => {
        //     addLog('NOTIF', `generic — ${data.message} [${data.type}]`);
        // });
        // notificationsSocket.on('notifications:batch', (data) => {
        //     addLog('NOTIF', `batch sync — received ${data.length} notifications`);
        //     data.forEach((n) => addLog('NOTIF', `  └ ${n.type}: ${JSON.stringify(n.payload)}`));
        // });

        return () => {
            collaborationSocket.removeAllListeners();
            // notificationsSocket.removeAllListeners();
            disconnectNamespace(collaborationSocket);
            // disconnectNamespace(notificationsSocket);
        };
    }, [addLog]);

    // ── Actions ──

    const handleConnect = () => {
        addLog('API', 'Connecting...');
        connectNamespace(collaborationSocket);
        // connectNamespace(notificationsSocket);
    };
    const handleDisconnect = () => {
        addLog('API', 'Disconnecting...');
        disconnectNamespace(collaborationSocket);
        // disconnectNamespace(notificationsSocket);
    };

    const handleJoinRoom = () => {
        if (!diagramId) return;
        addLog('ROOM', `Requesting join diagram:${diagramId}...`);
        collaborationSocket.emit('diagram:join', { diagramId }, (result) => {
            if (result.success) {
                setInRoom(diagramId);
                addLog('ROOM', `Joined diagram:${diagramId} (role: ${result.role})`);
            } else {
                addLog('ERROR', `Join failed: ${result.error}`);
            }
        });
    };

    const handleLeaveRoom = () => {
        if (!inRoom) return;
        collaborationSocket.emit('diagram:leave', { diagramId: inRoom });
        addLog('ROOM', `Left diagram:${inRoom}`);
        setInRoom(null);
        setRoomMembers([]);
    };

    const handleSendCursor = () => {
        if (!inRoom) return;
        const x = Math.round(Math.random() * 1000);
        const y = Math.round(Math.random() * 800);
        collaborationSocket.emit('cursor:move', { diagramId: inRoom, x, y });
        addLog('CURSOR', `Sent cursor (${x}, ${y}) to diagram:${inRoom}`);
    };

    const handleSendPresence = (status) => {
        if (!inRoom) return;
        collaborationSocket.emit('presence:update', { diagramId: inRoom, status });
        addLog('PRESENCE', `Sent presence "${status}" to diagram:${inRoom}`);
    };

    const handleSyncNotifications = (withTimestamp) => {
        const payload = withTimestamp
            ? { lastSeenTimestamp: new Date(Date.now() - 60 * 60 * 1000).toISOString() }
            : {};
        addLog('NOTIF', `Syncing notifications${withTimestamp ? ' (last hour)' : ' (unread)'}...`);
        // notificationsSocket.emit('notifications:sync', payload);
    };

    const handleTriggerNotification = async (type) => {
        try {
            addLog('API', `POST /test-socket/notification {type: "${type}"}...`);
            const { data } = await axios.post('/test-socket/notification', { type, diagramId: inRoom || 'test-diagram-1' });
            addLog('API', `Response: ${JSON.stringify(data)}`);
        } catch (err) {
            addLog('ERROR', `API error: ${err.response?.data?.error || err.message}`);
        }
    };

    const handleTriggerDiagramUpdate = async () => {
        try {
            const id = inRoom || 'test-diagram-1';
            addLog('API', `POST /test-socket/diagram-update {diagramId: "${id}"}...`);
            const { data } = await axios.post('/test-socket/diagram-update', { diagramId: id });
            addLog('API', `Response: ${JSON.stringify(data)}`);
        } catch (err) {
            addLog('ERROR', `API error: ${err.response?.data?.error || err.message}`);
        }
    };

    // ── Render ──

    return (
        <div style={{ background: colors.bg, color: colors.text, minHeight: '100vh', padding: 32, fontFamily: '-apple-system, monospace', fontSize: 14 }}>
            <h2 style={{ margin: '0 0 24px', fontWeight: 600 }}>Socket.IO E2E Test</h2>

            {/* ── 1. Connection ── */}
            <div style={card}>
                <div style={label}>1. Connection</div>
                <div style={{ display: 'flex', gap: 24, marginBottom: 12 }}>
                    <div><span style={dot(collabConnected)} />/collaboration: {collabConnected ? 'connected' : 'disconnected'}</div>
                    <div><span style={dot(notifConnected)} />/notifications: {notifConnected ? 'connected' : 'disconnected'}</div>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                    <button style={btn(colors.green)} onClick={handleConnect} disabled={collabConnected && notifConnected}>Connect</button>
                    <button style={btn(colors.red)} onClick={handleDisconnect} disabled={!collabConnected && !notifConnected}>Disconnect</button>
                </div>
            </div>

            {/* ── 2. Collaboration: Rooms ── */}
            <div style={card}>
                <div style={label}>2. Collaboration — Rooms</div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12 }}>
                    <input style={input} value={diagramId} onChange={(e) => setDiagramId(e.target.value)} placeholder="diagram ID" />
                    <button style={btn(colors.green)} onClick={handleJoinRoom} disabled={!collabConnected || !!inRoom}>Join Room</button>
                    <button style={btn(colors.red)} onClick={handleLeaveRoom} disabled={!inRoom}>Leave Room</button>
                </div>
                {inRoom && (
                    <div style={{ fontSize: 12, color: colors.muted }}>
                        In room: <span style={{ color: colors.blue }}>diagram:{inRoom}</span>
                        {roomMembers.length > 0 && <> | Members: {roomMembers.map((m) => m.userName).join(', ')}</>}
                    </div>
                )}
            </div>

            {/* ── 3. Collaboration: Cursors & Presence ── */}
            <div style={card}>
                <div style={label}>3. Collaboration — Cursors & Presence</div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <button style={btn(colors.yellow)} onClick={handleSendCursor} disabled={!inRoom}>Send Random Cursor</button>
                    <button style={btn(colors.yellow)} onClick={() => handleSendPresence('active')} disabled={!inRoom}>Presence: active</button>
                    <button style={btn(colors.yellow)} onClick={() => handleSendPresence('idle')} disabled={!inRoom}>Presence: idle</button>
                </div>
                {!inRoom && <div style={{ fontSize: 12, color: colors.muted, marginTop: 8 }}>Join a room first to send cursor/presence events</div>}
            </div>

            {/* ── 4. Notifications ── */}
            <div style={card}>
                <div style={label}>4. Notifications — Sync</div>
                <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                    <button style={btn(colors.purple)} onClick={() => handleSyncNotifications(false)} disabled={!notifConnected}>Sync Unread</button>
                    <button style={btn(colors.purple)} onClick={() => handleSyncNotifications(true)} disabled={!notifConnected}>Sync Last Hour</button>
                </div>

                <div style={{ ...label, marginTop: 12 }}>Trigger Notification (via REST → Event Bus → Socket)</div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <button style={btn(colors.purple)} onClick={() => handleTriggerNotification('invite')} disabled={!notifConnected}>invite</button>
                    <button style={btn(colors.purple)} onClick={() => handleTriggerNotification('member_added')} disabled={!notifConnected}>member_added</button>
                    <button style={btn(colors.purple)} onClick={() => handleTriggerNotification('member_removed')} disabled={!notifConnected}>member_removed</button>
                    <button style={btn(colors.purple)} onClick={() => handleTriggerNotification('diagram_updated')} disabled={!notifConnected}>diagram_updated</button>
                    <button style={btn(colors.purple)} onClick={() => handleTriggerNotification('generic')} disabled={!notifConnected}>generic</button>
                </div>
            </div>

            {/* ── 5. Event Bus → Socket (diagram:updated) ── */}
            <div style={card}>
                <div style={label}>5. Event Bus — Diagram Update (REST → EventBus → Collaboration Socket)</div>
                <button style={btn(colors.blue)} onClick={handleTriggerDiagramUpdate} disabled={!collabConnected}>Trigger diagram:updated</button>
                {!inRoom && <span style={{ fontSize: 12, color: colors.muted, marginLeft: 12 }}>Join a room to receive this event</span>}
            </div>

            {/* ── Event Log ── */}
            <div style={card}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={label}>Event Log ({logs.length})</div>
                    <button style={{ ...btn(), padding: '3px 10px', fontSize: 11 }} onClick={() => setLogs([])}>Clear</button>
                </div>
                <div ref={logRef} style={{ background: colors.bg, padding: 12, borderRadius: 6, maxHeight: 350, overflowY: 'auto', fontSize: 12, lineHeight: 1.7 }}>
                    {logs.length === 0 && <div style={{ color: colors.muted }}>No events yet. Click Connect to start.</div>}
                    {logs.map((log, i) => (
                        <div key={i} style={{ color: colorize(log) }}>{log}</div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default SocketTest;
